import store from "@/store";
import { selectUserEmail } from "@/store/userSlice";
import { getSessionId } from "@/config/sessionConfig";
import { buildCostcoEmail } from "@/pages/admin/RiskStatusThreshold/utils";
import { LogApi, TelemetryPayload, DiagnosticPayload } from "@/api/LogApi";
import { generateUUID } from "./UUIDHelper";
import axios, { AxiosError } from "axios";
import Logger from "@/utils/Logger";
import { LogSeverity, LogSource } from "@/constants/errorConstants";

/**
 * Type guard to check if error has response property (duck-typed AxiosError)
 */
const hasResponse = (
  error: unknown,
): error is { response: { status: number; data?: unknown } } => {
  return (
    typeof error === "object" &&
    error !== null &&
    "response" in error &&
    typeof (error as { response?: unknown }).response === "object" &&
    (error as { response?: { status?: unknown } }).response !== null &&
    typeof (error as { response: { status?: unknown } }).response.status ===
      "number"
  );
};

/**
 * Type guard to check if error has config property
 */
const hasConfig = (
  error: unknown,
): error is {
  config: { url?: string; method?: string; data?: unknown };
} => {
  return (
    typeof error === "object" &&
    error !== null &&
    "config" in error &&
    typeof (error as { config?: unknown }).config === "object" &&
    (error as { config?: unknown }).config !== null
  );
};

/**
 * Type guard to check if error is an AxiosError or looks like one
 */
const isAxiosError = (error: unknown): error is AxiosError => {
  return axios.isAxiosError(error) || hasResponse(error) || hasConfig(error);
};

/**
 * Type guard to check if error has a code property
 */
const hasCode = (error: unknown): error is { code: string } => {
  return (
    typeof error === "object" &&
    error !== null &&
    "code" in error &&
    typeof (error as { code?: unknown }).code === "string"
  );
};

/**
 * Type guard to check if error is a standard Error
 */
const isError = (error: unknown): error is Error => {
  return error instanceof Error;
};

/**
 * Extract error type from unknown error
 */
const getErrorType = (error: unknown): string => {
  if (hasCode(error)) {
    return error.code;
  }
  if (isError(error)) {
    return error.name || "Error";
  }
  return "UnknownError";
};

/**
 * Extract error code (HTTP status) from unknown error
 */
const getErrorCode = (error: unknown): number | undefined => {
  if (hasResponse(error)) {
    return error.response.status;
  }
  return undefined;
};

/**
 * Extract error message from unknown error
 */
const getErrorMessage = (error: unknown): string => {
  if (
    typeof error === "object" &&
    error !== null &&
    "message" in error &&
    typeof (error as { message?: unknown }).message === "string"
  ) {
    return (error as { message: string }).message;
  }
  if (typeof error === "string") {
    return error;
  }
  return "Unknown error occurred";
};

/**
 * Build stack trace string from unknown error
 * Matches backend DiagnosticLog.stackTrace schema
 */
const buildStackTrace = (error: unknown): string | undefined => {
  // For standard Error objects, return the stack trace
  if (
    typeof error === "object" &&
    error !== null &&
    "stack" in error &&
    typeof (error as { stack?: unknown }).stack === "string"
  ) {
    return (error as { stack: string }).stack;
  }

  return undefined;
};

/**
 * Determine log severity from unknown error
 * Maps to backend DiagnosticLog.severity enum
 */
const getLogSeverity = (error: unknown): LogSeverity => {
  if (hasResponse(error)) {
    const status = error.response.status;
    if (status >= 500) return LogSeverity.CRITICAL;
    if (status >= 400) return LogSeverity.ERROR;
    return LogSeverity.WARN;
  }
  return LogSeverity.ERROR;
};

/**
 * Determine root cause from unknown error
 */
const getRootCause = (error: unknown): string | undefined => {
  if (hasResponse(error)) {
    const status = error.response.status;
    if (status >= 500) {
      return "Server-side error occurred";
    }
    if (status >= 400) {
      return "Client request error";
    }
  }
  if (isAxiosError(error) && !hasResponse(error)) {
    return "Network connectivity issue";
  }
  return "Application error";
};

/**
 * Get user email from Redux store
 */
export const getUserEmail = (): string => {
  const state = store.getState();
  const username = selectUserEmail(state);
  const email = buildCostcoEmail(username);
  return email || "sysadmin@costco.com";
};

/**
 * Shared utility to get common metadata across all log types
 */
export const getCommonMetadata = () => {
  return {
    sessionId: getSessionId() || generateUUID(),
    userEmail: getUserEmail(),
    userAgent: navigator.userAgent,
    // TODO: Re-enable URL logging once Azure Front Door WAF is configured to allow full URLs
    // Currently blocked by WAF when full URLs (with protocol and domain) are sent
    // url: window.location.href,
    path: window.location.pathname, // Send only path instead of full URL for now
    timestamp: new Date().toISOString(),
  };
};

/**
 * Log telemetry (user activity) with standardized payload
 * Matches backend TelemetryLog schema exactly
 */
export const logTelemetry = async (
  pageName: string,
  additionalData?: Record<string, unknown>,
): Promise<void> => {
  try {
    const meta = getCommonMetadata();

    // Normalize all values to be objects (required by API schema)
    const normalizeValue = (val: unknown): object | null => {
      if (val === null || val === undefined) return null;
      if (typeof val === "object" && val !== null) return val;
      return { value: val };
    };

    // Transform all additionalData values to objects
    const normalizedData = Object.entries(additionalData || {}).reduce(
      (acc, [key, value]) => ({
        ...acc,
        [key]: normalizeValue(value),
      }),
      {} as Record<string, object | null>,
    );

    const payload: TelemetryPayload = {
      sessionId: meta.sessionId,
      userEmail: meta.userEmail,
      pageName,
      additionalData: {
        ...normalizedData,
        userAgent: { value: meta.userAgent },
        // TODO: Re-enable URL logging once Azure Front Door WAF is configured to allow full URLs
        // url: { value: meta.url },
        path: { value: meta.path }, // Send only path instead of full URL for now
        timestamp: { value: meta.timestamp },
      },
    };

    await LogApi.postTelemetry(payload);
  } catch {
    // Silent failure - telemetry should never disrupt the application
    Logger.debug(`Telemetry logging failed for page: ${pageName}`, [
      "LogHelper",
    ]);
  }
};

/**
 * Log diagnostic (error/system) with standardized payload
 * Matches backend DiagnosticLog schema exactly
 */
export const logDiagnostic = async (
  error: unknown,
  context: string,
): Promise<void> => {
  try {
    const meta = getCommonMetadata();

    const payload: DiagnosticPayload = {
      sessionId: meta.sessionId,
      userEmail: meta.userEmail,
      componentName: context,
      errorType: getErrorType(error),
      errorCode: getErrorCode(error),
      message: getErrorMessage(error),
      stackTrace: buildStackTrace(error),
      severity: getLogSeverity(error),
      source: LogSource.FRONTEND_UI,
      rootCause: getRootCause(error),
    };

    await LogApi.postDiagnostic(payload);
  } catch {
    // Silent failure - diagnostic logging should never disrupt the application
    Logger.debug(`Diagnostic logging failed for context: ${context}`, [
      "LogHelper",
    ]);
  }
};
